import { Private_Routes } from '@common/url';
import { clone } from '@prophecy/utils/data';
import { usePersistentCallback, useDebounce } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { getSourceEntities, setTranspilerBasicData } from 'frontend/core/src/data/apis/api';
import { useRestMutation } from 'frontend/core/src/data/util';
import { makePipelineNameUnique } from 'frontend/core/src/utils';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDeleteFile } from '../components/DeleteFile';
import {
  FileFormats,
  QueryKey,
  TranspilerEntityType,
  JobEntityType,
  PipelineEntityType,
  DatasetEntityType
} from '../config';
import { useTranspilerEnabledLanguages, useTypeSpecificLanguage } from '../hooks';
import { useUploadedFilePaths } from '../useUploadedFilePaths';
import { useUploadMutation } from '../useUploadMutation';
import { useTranspilerInitialState, useProjectBrowserState } from './hook';
import { FolderUploadTypes, ImportStepValue, ActionTypes } from './steps/types';
import { EntityErrorType, UseImportContext } from './types';
import { useBasicFormTypes, isLanguageSelectionEnabled } from './useBasicFormTypes';
import { getUniqueEntities, getEntityDataWithPath, isInformatica } from './utils';
import { useTranspilationGraph } from './webSocketHook';

export function useImportContext(uid?: string): UseImportContext {
  const { transpilerState, setTranspilerState } = useTranspilerInitialState('', uid);
  const transpilerId = transpilerState.transpilerId;
  const isReimport = Boolean(transpilerId && transpilerState.step);
  const formDataWithActions = useBasicFormTypes({ isReimport });
  const [pathToUpdate, setPathToUpdate] = useState(FolderUploadTypes.initial as string);
  const { paths, isLoading } = useUploadedFilePaths(
    transpilerId,
    pathToUpdate,
    Boolean(pathToUpdate && transpilerId),
    setPathToUpdate
  );

  const { removeFile, isDeleting } = useDeleteFile(transpilerId, setPathToUpdate);

  const projectBrowserState = useProjectBrowserState();

  const handleStepChange = usePersistentCallback(async (step?: number, isPrevious?: boolean) => {
    if (step === ImportStepValue.UploadFiles && !isPrevious) {
      await updateBasicFormData();
    } else if (step !== undefined) {
      setTranspilerState((draft) => {
        draft.activeStep = step;
        draft.isUpdate = step === ImportStepValue.TranspileGraph ? false : draft.isUpdate;
      });
    }
  });

  const {
    formContext: {
      getValues,
      watch,
      setValue,
      formState: { touchedFields }
    }
  } = formDataWithActions;

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const transpilerType = watch('source');
  const supportedLanguages = useTranspilerEnabledLanguages(transpilerType as FileFormats);

  const getTypeSpecificLanguage = useTypeSpecificLanguage();

  const handleTranspilerTypeChange = usePersistentCallback((transpilerType: FileFormats) => {
    setValue('source', transpilerType);
    setValue('language', getTypeSpecificLanguage(transpilerType, supportedLanguages || new Set()));
    setValue('isLanguageEnabled', isLanguageSelectionEnabled(transpilerType));
  });

  const resetTranspilerEntities = usePersistentCallback(async (type?: string) => {
    if (type) {
      await handleTranspilerTypeChange(type as FileFormats);
    }
    await queryClient.invalidateQueries([QueryKey]);
    setTranspilerState((draft) => {
      draft.isUpdate = false;
      draft.pipelineWithErrors = [];
      draft.datasetWithErrors = [];
      draft.jobWithErrors = [];
      draft.uploadedFilePaths = [];
      draft.transpilerEntityTypes = { jobs: [], pipelines: [], datasets: [] };
    });
    setPathToUpdate(FolderUploadTypes.success);
    navigate(
      Private_Routes.TranspilerImport.tab.getUrl(
        { transpileId: transpilerId },
        {
          source: type || getValues('source'),
          step: `${ImportStepValue.Sources}`
        }
      )
    );
  });

  const transpilerEntities = transpilerState.transpilerEntityTypes;

  const isAlteryx = transpilerType === FileFormats.alteryx;

  const setUploadedPaths = usePersistentCallback((uploadedPaths: string[]) => {
    setTranspilerState((draft) => {
      draft.uploadedFilePaths = uploadedPaths;
    });
    setPathToUpdate(FolderUploadTypes.success);
  });

  const updateTranspilationPreviewState = usePersistentCallback(
    (resp: TranspilerEntityType, isMount = false, requiresAssociatedEntities?: boolean) => {
      setTranspilerState((draft) => {
        const pipelines = draft.transpilerEntityTypes.pipelines;
        const jobs = draft.transpilerEntityTypes.jobs;
        const datasets = draft.transpilerEntityTypes.datasets || [];
        const statePipelines = clone(resp?.pipelines || []);
        const stateDatasets = clone(resp?.datasets || []);
        const stateJobs = clone(resp?.jobs || []);
        draft.isUpdate = !isMount;
        const currentPipelines = isMount ? statePipelines : getUniqueEntities(pipelines, statePipelines);
        const currentJobs = isMount ? stateJobs : getUniqueEntities(jobs, stateJobs);
        const currentDatasets = isMount ? stateDatasets : getUniqueEntities(datasets, stateDatasets, true);
        const [filteredJobs, jobWithErrors] = getEntityDataWithPath(currentJobs as JobEntityType[]);
        const [filteredPipelines, pipelineWithErrors] = getEntityDataWithPath(currentPipelines as PipelineEntityType[]);
        draft.transpilerEntityTypes = {
          pipelines: filteredPipelines as PipelineEntityType[],
          jobs: filteredJobs as JobEntityType[],
          datasets: currentDatasets as DatasetEntityType[]
        };
        draft.pipelineWithErrors = pipelineWithErrors as EntityErrorType[];
        draft.jobWithErrors = jobWithErrors as EntityErrorType[];
      });

      if (requiresAssociatedEntities || isAlteryx) {
        getAssociatedEntitiesForPanel();
      }
    }
  );

  const setDatasets = usePersistentCallback(
    (datasetsResponse: DatasetEntityType[], associatedEntities: { [key: string]: string[] }) => {
      const currentDatasets: DatasetEntityType[] = [...(transpilerEntities.datasets || [])];
      if (datasetsResponse.length > 0) {
        datasetsResponse.forEach((datasetResponse) => {
          const { id, name } = datasetResponse;
          const hasDataset = currentDatasets.find((dataset) => dataset.id === id);
          if (!hasDataset) {
            currentDatasets.push({
              ...datasetResponse,
              pipelines: associatedEntities[name] || []
            });
          }
        });
      }

      setTranspilerState((draft) => {
        draft.transpilerEntityTypes.datasets = currentDatasets;
      });
    }
  );

  const transpileGraphState = useTranspilationGraph({
    transpilerId,
    step: transpilerState.activeStep,
    isReimport,
    language: getValues('language'),
    handleStepChange,
    resetTranspilerEntities,
    transpilerType,
    isUpdate: transpilerState.isUpdate,
    transpilerEntities,
    setUploadedPaths,
    setDatasets,
    setPipelineDatasets: updateTranspilationPreviewState
  });

  const {
    actions: { resetState, setUploadPercentage, resetUploadProgress, getAssociatedEntities }
  } = transpileGraphState;

  const uploadMutationState = useUploadMutation(
    transpilerId,
    setPathToUpdate,
    setUploadPercentage,
    resetUploadProgress
  );

  useEffect(() => {
    setTranspilerState((draft) => {
      draft.uploadedFilePaths = paths;
    });
  }, [paths, setTranspilerState]);

  const getAssociatedEntitiesForPanel = useDebounce((isAllDelete?: boolean) => {
    const { jobs, pipelines } = transpilerEntities;
    // if not alteryx or abinitio, return early
    if (transpilerType !== FileFormats.alteryx && transpilerType !== FileFormats.abinitio) {
      return;
    }
    if (jobs.length > 0 || pipelines.length > 0 || isAllDelete) {
      getAssociatedEntities();
    }
  }, 10);

  const getSourceFilesMutation = useRestMutation(
    (filePath: string) =>
      getSourceEntities(transpilerId, {
        filePath,
        transpilerType
      }),
    {
      retry: false,
      sentryTag: 'transpiler.informaticaPipelines',
      onSuccess: (resp) => {
        setTranspilerState((draft) => {
          draft.isUpdate = true;
          const allPipelines = [...draft.transpilerEntityTypes.pipelines, ...resp.data.pipelines];
          makePipelineNameUnique(allPipelines as PipelineEntityType[]);
          const [draftPipelines, pipelineWithErrors] = getEntityDataWithPath(allPipelines as PipelineEntityType[]);
          draft.transpilerEntityTypes.pipelines = draftPipelines as PipelineEntityType[];
          draft.pipelineWithErrors = pipelineWithErrors as EntityErrorType[];
          const [draftJobs, jobsWithErrors] = getEntityDataWithPath([
            ...draft.transpilerEntityTypes.jobs,
            ...resp.data.jobs
          ]);
          draft.transpilerEntityTypes.jobs = draftJobs as JobEntityType[];
          draft.jobWithErrors = jobsWithErrors as EntityErrorType[];
        });
        getAssociatedEntitiesForPanel();
      }
    }
  );

  const updateTranspilerFormMutation = useRestMutation(() => setTranspilerBasicData(getValues(), transpilerId), {
    retry: false,
    sentryTag: 'transpiler.updateBasicFormData'
  });

  const updateBasicFormData = async () => {
    if (Object.keys(touchedFields).length > 0 || !transpilerId) {
      await updateTranspilerFormMutation.mutateAsync(
        { formData: getValues() },
        {
          onSuccess: (resp) => {
            const id = resp?.data?.transpilerId;
            setTranspilerState((draft) => {
              draft.activeStep = ImportStepValue.UploadFiles;
              if (!isReimport && id) {
                draft.transpilerId = id as string;
              }
            });
          }
        }
      );
    } else {
      setTranspilerState((draft) => {
        draft.activeStep = ImportStepValue.UploadFiles;
      });
    }
  };

  const updateTranspilerDataset = usePersistentCallback((dataset: DatasetEntityType, index: number) => {
    let requiresAssociatedEntities = true;
    setTranspilerState((draft) => {
      const datasets = draft.transpilerEntityTypes.datasets || [];
      draft.isUpdate = true;
      const oldDataset = draft.transpilerEntityTypes.datasets[index] || ({} as DatasetEntityType);
      const oldDatasetName = oldDataset.name;
      draft.transpilerEntityTypes.datasets[index] = dataset;
      if (oldDatasetName !== dataset.name) {
        draft.transpilerEntityTypes.datasets = datasets;
      }
      if (oldDatasetName === dataset.name) {
        requiresAssociatedEntities = false;
      }
    });
    //Reset graph completed/failed state if chosen entities changed
    resetState();

    //Get associated entities
    if (requiresAssociatedEntities) {
      getAssociatedEntitiesForPanel(true);
    }
  });

  const updateTranspilerJob = usePersistentCallback(
    (job: JobEntityType, index: number, actionType?: ActionTypes, isAll?: boolean) => {
      const isDeleteAll = actionType === ActionTypes.deleteAll;
      const isDelete = actionType === ActionTypes.delete;
      let requiresAssociatedEntities = true;
      setTranspilerState((draft) => {
        const jobs = draft.transpilerEntityTypes.jobs;
        draft.isUpdate = true;
        if (actionType === ActionTypes.all) {
          requiresAssociatedEntities = false;
          draft.transpilerEntityTypes.jobs = jobs.map((job, i) => {
            job.enabled = !!isAll;
            job.__pathIndex = i;
            return job;
          });
        } else if (isDeleteAll) {
          draft.transpilerEntityTypes.jobs = [];
        } else if (isDelete) {
          jobs.splice(index, 1);
          draft.transpilerEntityTypes.jobs = jobs.map((job, i) => {
            job.__pathIndex = i;
            return job;
          });
        } else {
          const oldJob = draft.transpilerEntityTypes.jobs[index];
          const oldJobName = oldJob.name;
          draft.transpilerEntityTypes.jobs[index] = job;
          if (oldJobName !== job.name) {
            const [currentJobs, jobWithErrors] = getEntityDataWithPath(draft.transpilerEntityTypes.jobs);
            draft.transpilerEntityTypes.jobs = currentJobs as JobEntityType[];
            draft.jobWithErrors = jobWithErrors as EntityErrorType[];
          }
          if (oldJobName === job.name && oldJob.filePath === job.filePath) {
            requiresAssociatedEntities = false;
          }
        }
      });
      //Reset graph completed/failed state if chosen entities changed
      resetState();

      //Get associated entities
      if (requiresAssociatedEntities) {
        getAssociatedEntitiesForPanel(isDeleteAll || isDelete);
      }
    }
  );

  const getUpdatedDatasetsOnPipelineDeletion = usePersistentCallback(
    (pipelineName: string, datasets: DatasetEntityType[]) => {
      const updatedDatasets: DatasetEntityType[] = [];
      datasets.forEach((dataset) => {
        const associatedPipelines = (dataset.pipelines || []).filter((pipelineName1) => pipelineName1 !== pipelineName);
        if (associatedPipelines.length > 0) {
          updatedDatasets.push(dataset);
        }
      });
      return updatedDatasets;
    }
  );

  const updateTranspiledPipeline = usePersistentCallback(
    (index: number, pipeline?: PipelineEntityType, actionType?: ActionTypes, isAll?: boolean) => {
      const source = transpilerType;
      let requiresAssociatedEntities = true;
      const isInformaticaDatastageAdd =
        (isInformatica(source as FileFormats) || source === FileFormats.datastage) && actionType === ActionTypes.add;
      const isDeleteAll = actionType === ActionTypes.deleteAll;
      const isDelete = actionType === ActionTypes.delete;
      if (isInformaticaDatastageAdd) {
        requiresAssociatedEntities = false;
        getSourceFilesMutation.mutateAsync(pipeline?.filePath as string);
      } else {
        setTranspilerState((draft) => {
          const pipelines = draft.transpilerEntityTypes.pipelines;
          draft.isUpdate = true;
          if (actionType === ActionTypes.all) {
            requiresAssociatedEntities = false;
            draft.transpilerEntityTypes.pipelines = pipelines.map((pipeline, i) => {
              pipeline.enabled = !!isAll;
              pipeline.__pathIndex = i;
              return pipeline;
            });
          } else if (isDeleteAll) {
            draft.transpilerEntityTypes.pipelines = [];
            draft.transpilerEntityTypes.datasets = [];
          } else if (isDelete) {
            const updatedDatasets = getUpdatedDatasetsOnPipelineDeletion(
              pipelines[index]?.name,
              draft.transpilerEntityTypes.datasets
            );
            pipelines.splice(index, 1);
            draft.transpilerEntityTypes.pipelines = pipelines.map((pipeline, i) => {
              pipeline.__pathIndex = i;
              return pipeline;
            });
            draft.transpilerEntityTypes.datasets = updatedDatasets;
          } else if (actionType === ActionTypes.add && pipeline) {
            draft.transpilerEntityTypes.pipelines.push({ ...pipeline, transpileInterfaceTools: true });
          } else if (pipeline) {
            const oldPipeline = draft.transpilerEntityTypes.pipelines[index];
            const oldPipelineName = oldPipeline.name;
            draft.transpilerEntityTypes.pipelines[index] = pipeline;
            if (!oldPipeline.preserveVisualContainers && pipeline.breakPipelinesToSmallerPipelines) {
              pipeline.preserveVisualContainers = true;
            } else if (oldPipeline.preserveVisualContainers && !pipeline.breakPipelinesToSmallerPipelines) {
              pipeline.preserveVisualContainers = false;
            }
            if (oldPipelineName !== pipeline.name) {
              const [currentPipelines, pipelineWithErrors] = getEntityDataWithPath(
                draft.transpilerEntityTypes.pipelines
              );
              draft.transpilerEntityTypes.pipelines = currentPipelines as PipelineEntityType[];
              draft.pipelineWithErrors = pipelineWithErrors as EntityErrorType[];
            }
            if (oldPipelineName === pipeline.name && oldPipeline.filePath === pipeline.filePath) {
              requiresAssociatedEntities = false;
            }
          }
        });
      }
      //Reset graph completed/failed state if chosen entities changed
      resetState();

      //getAssociated entities
      if (requiresAssociatedEntities) {
        getAssociatedEntitiesForPanel(isDeleteAll || isDelete);
      }
    }
  );

  const addAllPipelines = usePersistentCallback((pipelines: PipelineEntityType[]) => {
    setTranspilerState((draft) => {
      draft.transpilerEntityTypes.pipelines = [...draft.transpilerEntityTypes.pipelines, ...pipelines];
      draft.isUpdate = true;
    });
    //Reset graph completed/failed state if chosen entities changed
    resetState();

    //getAssociated entities
    getAssociatedEntitiesForPanel();
  });

  return useMemo(
    () =>
      ({
        transpilerState,
        isReimport,
        formDataWithActions,
        isDeleting,
        loading:
          updateTranspilerFormMutation.isLoading ||
          getSourceFilesMutation.isLoading ||
          (pathToUpdate && pathToUpdate !== FolderUploadTypes.initial) ||
          uploadMutationState.uploadMutation.isLoading,
        browserDataLoading: isLoading,
        projectBrowserState,
        uploadMutationState,
        transpileGraphState,
        actions: {
          updateTranspilationPreviewState,
          handleStepChange,
          updateTranspilerDataset,
          removeFile,
          addAllPipelines,
          updateTranspilerJob,
          updateTranspiledPipeline,
          handleTranspilerTypeChange
        }
      }) satisfies UseImportContext,
    [
      isReimport,
      isDeleting,
      removeFile,
      addAllPipelines,
      updateTranspilerDataset,
      projectBrowserState,
      transpileGraphState,
      updateTranspilationPreviewState,
      formDataWithActions,
      updateTranspilerJob,
      updateTranspiledPipeline,
      handleStepChange,
      updateTranspilerFormMutation.isLoading,
      getSourceFilesMutation.isLoading,
      isLoading,
      pathToUpdate,
      handleTranspilerTypeChange,
      uploadMutationState,
      transpilerState
    ]
  );
}
